import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../contexts/toast/Context";
import { MANAGER_GATEWAY_CONFIG_QUERY_KEY } from "../../query/keys";
import { MP_CONNECT_GENERIC_ERROR } from "../requests/gym";

const OAUTH_RESULT_TOASTS: Record<
  string,
  { message: string; type: "success" | "error" }
> = {
  connected: {
    message: "Mercado Pago conectado. Ya puedes cobrar online.",
    type: "success",
  },
  access_denied: {
    message: "Cancelaste la autorización en Mercado Pago.",
    type: "error",
  },
  account_already_linked: {
    message: "Esa cuenta de Mercado Pago ya está conectada a otro gimnasio.",
    type: "error",
  },
  invalid_state: {
    message: "La conexión expiró. Intenta de nuevo.",
    type: "error",
  },
};

/**
 * Procesa el retorno del onboarding OAuth de Mercado Pago
 * (?mp=connected | ?mp=error&code=...): muestra el toast, refresca el estado
 * de pasarelas y limpia los parámetros de la URL. Side-effect de montaje.
 */
export const useOAuthConnectionResult = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const handled = useRef(false);

  useEffect(() => {
    const mp = searchParams.get("mp");
    if (!mp || handled.current) return;
    handled.current = true;

    const key =
      mp === "connected" ? "connected" : (searchParams.get("code") ?? "");
    const toast = Object.hasOwn(OAUTH_RESULT_TOASTS, key)
      ? OAUTH_RESULT_TOASTS[key]!
      : { message: MP_CONNECT_GENERIC_ERROR, type: "error" as const };
    showToast(toast.message, toast.type);

    if (mp === "connected") {
      queryClient.invalidateQueries({
        queryKey: MANAGER_GATEWAY_CONFIG_QUERY_KEY,
      });
    }

    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete("mp");
    cleaned.delete("code");
    setSearchParams(cleaned, { replace: true });
  }, [searchParams, setSearchParams, showToast, queryClient]);
};
